import { useEffect } from 'react';

import { type DynamicYieldCampaign } from '../../../../shared/types';

interface Props {
    readonly creative: DynamicYieldCampaign;
}

export const DynamicYieldServerRenderedBanner = ({ creative }: Props): JSX.Element | null => {
    const { analyticsMetadata, renderedTemplate } = creative;

    const styleId = `dy-campaign-${analyticsMetadata.campaignId}-${analyticsMetadata.variationId}`;

    // Execute pre-rendered JavaScript without eval
    useEffect(() => {
        if (!renderedTemplate?.js) return undefined;

        const scriptId = `${styleId}-script`;
        const existing = document.querySelector(`#${scriptId}`);
        if (existing && existing.parentNode) existing.parentNode.removeChild(existing);

        const script = document.createElement('script');
        script.type = 'text/javascript';
        script.id = scriptId;
        // Optional CSP nonce support if present on the page
        const nonceMeta =
            (document.querySelector('meta[name="csp-nonce"]') as HTMLMetaElement | null) ||
            (document.querySelector('meta[property="csp-nonce"]') as HTMLMetaElement | null);
        if (nonceMeta?.content) {
            (script as unknown as { nonce?: string }).nonce = nonceMeta.content;
        }

        script.text = renderedTemplate.js;

        // Prefer head to avoid blocking layout; body is fine as fallback
        const parent = document.head || document.body || document.documentElement;
        parent.appendChild(script);

        return () => {
            if (script.parentNode) script.parentNode.removeChild(script);
        };
    }, [renderedTemplate?.js, styleId]);

    if (!renderedTemplate?.html) {
        return null;
    }

    return (
        <>
            {renderedTemplate.css ? <style id={styleId}>{renderedTemplate.css}</style> : null}
            <div
                data-dy-campaign-id={analyticsMetadata.campaignId}
                data-dy-campaign-name={analyticsMetadata.campaignName}
                data-dy-experience-id={analyticsMetadata.experienceId}
                data-dy-experience-name={analyticsMetadata.experienceName}
                data-dy-variation-id={analyticsMetadata.variationId}
                data-dy-variation-name={analyticsMetadata.variationName}
                // eslint-disable-next-line react/no-danger
                dangerouslySetInnerHTML={{ __html: renderedTemplate.html }}
            />
        </>
    );
};
